import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useMemo,
  useRef,
} from "react";
import openSocket from "../../services/socket-io";
import { Inbox } from "lucide-react";

import TicketListItem from "../TicketListItem";
import usePresence from "../../hooks/usePresence";
import useWhatsApps from "../../hooks/useWhatsApps";
import usePermissions from "../../hooks/usePermissions";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_TICKETS") {
    const newTickets = action.payload;

    newTickets.forEach((ticket) => {
      const ticketIndex = state.findIndex((t) => t.id === ticket.id);
      if (ticketIndex !== -1) {
        state[ticketIndex] = ticket;
        if (ticket.unreadMessages > 0) {
          state.unshift(state.splice(ticketIndex, 1)[0]);
        }
      } else {
        state.push(ticket);
      }
    });

    return [...state];
  }

  if (action.type === "RESET_UNREAD") {
    const ticketId = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticketId);
    if (ticketIndex !== -1) {
      state[ticketIndex].unreadMessages = 0;
    }

    return [...state];
  }

  if (action.type === "UPDATE_TICKET") {
    const ticket = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticket.id);
    if (ticketIndex !== -1) {
      state[ticketIndex] = ticket;
    } else {
      state.unshift(ticket);
    }

    return [...state];
  }

  if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
    const ticket = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticket.id);
    if (ticketIndex !== -1) {
      state[ticketIndex] = ticket;
      state.unshift(state.splice(ticketIndex, 1)[0]);
    } else {
      state.unshift(ticket);
    }

    return [...state];
  }

  if (action.type === "UPDATE_TICKET_CONTACT") {
    const contact = action.payload;
    const ticketIndex = state.findIndex((t) => t.contactId === contact.id);
    if (ticketIndex !== -1) {
      state[ticketIndex].contact = contact;
    }
    return [...state];
  }

  if (action.type === "DELETE_TICKET") {
    const ticketId = action.payload;
    const ticketIndex = state.findIndex((t) => t.id === ticketId);
    if (ticketIndex !== -1) {
      state.splice(ticketIndex, 1);
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const TicketsList = (props) => {
  const {
    status,
    searchParam,
    showAll,
    selectedQueueIds,
    updateCount,
    style,
    isGroup,
    vazio,
    ordemAZ,
    tagIds,
    userIds,
    whatsappIds,
    dataInicio,
    dataFim,
    visao,
    onlyFavorites,
    ultimosDias,
    agruparPorContato,
  } = props;
  const [pageNumber, setPageNumber] = useState(1);
  const [ticketsList, dispatch] = useReducer(reducer, []);
  const { user } = useContext(AuthContext);
  // Recorte de tickets do cargo. Só quem enxerga a empresa inteira pode ver
  // grupo fora das próprias filas — ver o comentário na conferência de fila.
  const { ticketScope } = usePermissions();

  // A aba pode pedir mais de uma situação ("open,pending" na de Grupos:
  // ninguém "aceita" uma conversa de grupo, então ela vive nas duas). Cada
  // sala do Socket.IO é de UMA situação — entrar numa sala chamada
  // "open,pending" é entrar numa sala para onde o servidor nunca emite, e a
  // aba só se mexia com F5.
  const situacoes = useMemo(
    () =>
      String(status || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    [status],
  );

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [
    situacoes,
    searchParam,
    dispatch,
    showAll,
    selectedQueueIds,
    isGroup,
    visao,
    onlyFavorites,
  ]);

  const { tickets, hasMore, loading } = useTickets({
    pageNumber,
    searchParam,
    status,
    showAll,
    queueIds: JSON.stringify(selectedQueueIds),
    isGroup,
    tagIds,
    userIds,
    whatsappIds,
    dataInicio,
    dataFim,
    visao,
    onlyFavorites,
    ultimosDias,
    agruparPorContato,
  });

  useEffect(() => {
    if (!status && !searchParam) return;
    dispatch({
      type: "LOAD_TICKETS",
      payload: tickets,
    });
  }, [tickets]);

  // O que já está na tela. O evento do socket carrega o ticket "cru", igual
  // para todo mundo: ele não diz se é favorito de quem está olhando nem de
  // quem é o recorte da aba. Guardar os ids visíveis é o que permite decidir
  // isso sem uma ida ao servidor por evento.
  const idsVisiveis = useRef([]);

  useEffect(() => {
    idsVisiveis.current = ticketsList.map((t) => t.id);
  }, [ticketsList]);

  useEffect(() => {
    const socket = openSocket();

    // O filtro de grupo vale também para o tempo real: sem isso um ticket de
    // grupo apareceria na aba de conversas individuais (e vice-versa).
    const matchesGroupFilter = (ticket) =>
      isGroup === undefined || Boolean(ticket.isGroup) === (isGroup === "true");

    // Os filtros da barra valem também para o que chega em tempo real. Sem
    // isto, um ticket novo entrava na lista mesmo sem a etiqueta (ou a
    // conexão, ou o atendente) filtrada: a pessoa filtrava por uma etiqueta e
    // via aparecer conversa que não tinha nada a ver com ela.
    const matchesTagFilter = (ticket) =>
      !tagIds?.length ||
      (ticket.tags || []).some((tag) => tagIds.includes(tag.id));

    const matchesWhatsappFilter = (ticket) =>
      !whatsappIds?.length || whatsappIds.includes(ticket.whatsappId);

    const matchesUserFilter = (ticket) =>
      !userIds?.length || userIds.includes(ticket.userId);

    // Cada lista é de um status só. A conexão do Socket.IO é uma para o app
    // inteiro, então TODA lista recebe o evento de TODO ticket — sem esta
    // conferência, uma mensagem nova num atendimento já aceito reinseria ele
    // na aba "Aguardando", e o mesmo cliente aparecia nas duas ao mesmo tempo.
    const matchesStatus = (ticket) =>
      !situacoes.length || situacoes.includes(ticket.status);

    // A aba Grupos não filtra por fila para quem enxerga a empresa inteira: o
    // grupo com fila fixa sumia para quem não tinha aquela fila. É a mesma
    // regra do ListTicketsService — se as duas divergirem, a linha aparece no
    // F5 e não em tempo real (ou o contrário).
    const ignoraFila = isGroup === "true" && ticketScope === "company";

    // Grupo é conversa da equipe: quem já enxerga além do próprio nome vê o
    // grupo assumido pelo colega.
    const grupoSemDono = isGroup === "true" && ticketScope !== "own";

    const matchesQueueFilter = (ticket) =>
      ignoraFila ||
      !ticket.queueId ||
      selectedQueueIds.indexOf(ticket.queueId) > -1;

    // Eixo dono/setor: "Minhas" só aceita o que está na mão de quem entrou;
    // "Do setor" aceita conversa de colega — é justamente o que ela mostra.
    const matchesVisao = (ticket) =>
      visao !== "minhas" || ticket.userId === user?.id;

    // Sem saber se é favorito, o critério seguro é "já estava na lista":
    // conversa recém-favoritada entra na próxima carga, e nenhuma
    // não-favorita se infiltra pelo tempo real.
    const matchesFavorito = (ticket) =>
      !onlyFavorites || idsVisiveis.current.includes(ticket.id);

    const shouldUpdateTicket = (ticket) =>
      !searchParam &&
      matchesStatus(ticket) &&
      matchesVisao(ticket) &&
      matchesFavorito(ticket) &&
      matchesGroupFilter(ticket) &&
      matchesTagFilter(ticket) &&
      matchesWhatsappFilter(ticket) &&
      matchesUserFilter(ticket) &&
      // Grupo não tem dono: a conversa é da equipe. Sem esta exceção, o
      // grupo que um colega assumiu sumia da aba Grupos até alguém ligar o
      // olho do "mostrar todos". Fora, de novo, o escopo "own" — a mesma
      // ressalva do ListTicketsService, senão a linha entra em tempo real e
      // some no F5 seguinte.
      (grupoSemDono ||
        visao === "setor" ||
        !ticket.userId ||
        ticket.userId === user?.id ||
        showAll) &&
      matchesQueueFilter(ticket);

    const notBelongsToUserQueues = (ticket) => !matchesQueueFilter(ticket);

    socket.on("connect", () => {
      if (situacoes.length) {
        situacoes.forEach((situacao) => socket.emit("joinTickets", situacao));
      } else {
        socket.emit("joinNotification");
      }
    });

    socket.on("ticket", (data) => {
      if (data.action === "updateUnread") {
        dispatch({
          type: "RESET_UNREAD",
          payload: data.ticketId,
        });
      }

      // Cada lista mostra um status. Quando o ticket muda de status ele
      // tem de sair desta lista e entrar na outra — antes ficava nas duas,
      // e os contadores das abas paravam de bater.
      if (
        data.action === "update" &&
        situacoes.length &&
        !situacoes.includes(data.ticket.status)
      ) {
        dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
      } else if (data.action === "update" && shouldUpdateTicket(data.ticket)) {
        dispatch({
          type: "UPDATE_TICKET",
          payload: data.ticket,
        });
      }

      // Na aba "Minhas", perder o dono (transferência, ou um colega que
      // puxou a conversa pela aba do setor) tem de tirar a linha da tela na
      // hora — senão ela continua ali dando a impressão de que o trabalho
      // ainda é meu.
      if (
        data.action === "update" &&
        visao === "minhas" &&
        data.ticket.userId !== user?.id
      ) {
        dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
      }

      if (data.action === "update" && notBelongsToUserQueues(data.ticket)) {
        dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
      }
    });

    socket.on("appMessage", (data) => {
      if (data.action === "create" && shouldUpdateTicket(data.ticket)) {
        dispatch({
          type: "UPDATE_TICKET_UNREAD_MESSAGES",
          payload: data.ticket,
        });
      }
    });

    socket.on("contact", (data) => {
      if (data.action === "update") {
        dispatch({
          type: "UPDATE_TICKET_CONTACT",
          payload: data.contact,
        });
      }
    });

    return () => {
      socket.disconnect();
    };
    // Os filtros entram nas dependências: sem eles os ouvintes seguiriam
    // enxergando a seleção de quando foram registrados, e voltariam a deixar
    // passar ticket que o filtro atual exclui.
  }, [
    situacoes,
    searchParam,
    showAll,
    user,
    ticketScope,
    selectedQueueIds,
    isGroup,
    tagIds,
    userIds,
    whatsappIds,
    visao,
    onlyFavorites
  ]);

  // Filtro novo = lista nova: sem zerar, os resultados antigos ficariam
  // misturados com os do filtro.
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [tagIds, userIds, whatsappIds, dataInicio, dataFim]);

  useEffect(() => {
    if (typeof updateCount === "function") {
      updateCount(ticketsList.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketsList]);

  // Ordem alfabética é uma visão do que já foi carregado; a ordem padrão
  // (mais recente primeiro) continua vindo do servidor.
  const listaVisivel = useMemo(() => {
    if (!ordemAZ) return ticketsList;

    return [...ticketsList].sort((a, b) =>
      (a.contact?.name || "").localeCompare(b.contact?.name || "", "pt-BR"),
    );
  }, [ticketsList, ordemAZ]);

  // Um socket de presença para a lista inteira, não um por item.
  const presencas = usePresence();

  // A conexão só aparece no item quando a empresa tem mais de uma — com
  // uma só, o nome se repetiria em todas as linhas sem informar nada.
  const { whatsApps } = useWhatsApps();
  const mostrarConexao = (whatsApps || []).length > 1;

  // O tempo de espera precisa envelhecer na tela; sem isto "2 min" ficaria
  // congelado até a próxima mensagem.
  const [, redesenhar] = useState(0);

  useEffect(() => {
    const t = setInterval(() => redesenhar((n) => n + 1), 30000);

    return () => clearInterval(t);
  }, []);

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      e.currentTarget.scrollTop = scrollTop - 100;
      loadMore();
    }
  };

  return (
    <div
      className="relative flex h-full flex-col overflow-hidden"
      style={style}
    >
      <div
        className="scrollbar-discreta flex-1 overflow-y-auto"
        onScroll={handleScroll}
      >
        {ticketsList.length === 0 && !loading ? (
          <div className="flex h-full flex-col items-center justify-center gap-1.5 px-8 py-16 text-center">
            <div className="mb-1 flex h-11 w-11 items-center justify-center rounded-full bg-muted">
              <Inbox className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium">
              {vazio?.titulo || i18n.t("ticketsList.noTicketsTitle")}
            </p>
            <p className="max-w-60 text-xs leading-relaxed text-muted-foreground">
              {vazio?.texto || i18n.t("ticketsList.noTicketsMessage")}
            </p>
          </div>
        ) : (
          listaVisivel.map((ticket) => (
            <TicketListItem
              ticket={ticket}
              key={ticket.id}
              presenca={presencas[ticket.id]}
              mostrarConexao={mostrarConexao}
            />
          ))
        )}
        {loading && <TicketsListSkeleton />}
      </div>
    </div>
  );
};

export default TicketsList;
